import {
  Card,
  TextInput,
  Button,
  Select,
  Label,
  Popover,
  ButtonGroup,
  Spinner,
} from "flowbite-react";
import { HiPlus, HiTrash } from "react-icons/hi";
import { FileManager } from "../components";
import { useEffect } from "react";
import styled from "styled-components";
import useMagazines from "../hooks/useMagazines";
import { useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { magazineSchema } from "../schemas/magazine.schema";
import { Attachment } from "../types/attachment";
import { useReducerAsState } from "../hooks/useReducerAsState";
import { toast } from "react-toastify";
import { routes } from "../routes";
import { useAuth } from "../context/authContext";
import { AUTH_URL } from "../constants";

interface ImageProps {
  src: string;
}

const Image = styled.div<ImageProps>`
  width: 250px;
  height: 320px;
  background-color: gray;

  ${({ src }) =>
    src !== "" &&
    `
        background-image: url("${src}");
        background-size: cover;
        background-repeat:no-repeat;
        background-position: center center;
    `}
`;

const Overlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 10000;
  background-color: rgba(255, 255, 255, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ImageContainer = styled.div`
  display: flex;
  align-items: center;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 4;
  padding: 12px;

  .magazine-item {
    width: calc(100% / 5);
    margin-bottom: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;

    @media only screen and (max-width: 1440px) {
      width: calc(100% / 3);
    }

    @media only screen and (max-width: 1024px) {
      width: 100%;
    }

    .actions {
      margin-top: 8px;
    }
  }
`;

const StyledModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 10000;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 200px;
  flex-direction: column;

  h3 {
    color: white;
    font-size: 30px;
    text-align: center;
    font-weight: bold;
    display: block;
    margin-bottom: 20px;
  }
`;

interface MagazineItem {
  fileId: number;
  url?: string;
}

enum UploadFileType {
  ADD_ATTACHMENT = "ATTACHMENT",
  ADD_PDF = "PDF",
  ADD_IMAGE = "IMAGE",
}

interface State {
  attachment: Attachment | null;
  pdfAttachment: Attachment | null;
  images: Array<MagazineItem>; // file ids
  showFileManager: boolean;
  imageIndex: number;
  uploadType: UploadFileType | null;
  loading: boolean;
  isAdobeService: boolean;
  canFetchAdobeStatus: boolean;
}

const MagazineDetailsPage = () => {
  const { permissions } = useAuth();

  const canCreate = permissions.find(
    (item) => item.slug === "crear-edicion-impresa"
  );
  const canEdit = permissions.find(
    (item) => item.slug === "editar-edicion-impresa"
  );

  const { id } = useParams();
  const {
    magazineCategories,
    fetchMagazineCategories,
    findMagazine,
    createMagazine,
    updateMagazine,
    deleteMagazineAttachment,
    loadingMagazines,
    triggerMagazineDownload,
  } = useMagazines();

  const navigate = useNavigate();

  const [state, setState] = useReducerAsState<State>({
    attachment: null,
    pdfAttachment: null,
    images: [],
    showFileManager: false,
    imageIndex: -1,
    uploadType: null,
    loading: false,
    isAdobeService: false,
    canFetchAdobeStatus: false,
  });

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(magazineSchema),
  });

  const handleShowFileManager = () =>
    setState({ showFileManager: !state.showFileManager });

  const setCanFetchAdobeStatus = (value: boolean) =>
    setState({ canFetchAdobeStatus: value });

  const save = async (data: any) => {
    if (state.loading) return;

    setState({ loading: true });
    const info = {
      id,
      name: data.name,
      image: data.image,
      published: data.published,
      category: data.category,
      pdfFile: state.pdfAttachment ? state.pdfAttachment.id : "",
      images: state.images,
      exportType: data.exportType,
    };

    if (data.exportType === "adobe") {
      // Show modal
      setState({ isAdobeService: true });
    }

    if (id === "create" && canCreate) {
      const response = await createMagazine(info);
      const hasErrors = response.length > 0;
      setState({ loading: false });

      if (data.exportType === "system") {
        toast(hasErrors ? response : "Edición guardada correctamente", {
          type: hasErrors ? "error" : "success",
        });
        setState({ loading: false });

        if (hasErrors) {
          return;
        }

        navigate(`${routes.DASHBOARD}/${routes.MAGAZINES}`, {
          replace: true,
        });
        return;
      }

      if (data.exportType === "adobe") {
        setCanFetchAdobeStatus(true);
      }
      return;
    }

    if (id !== "create" && canEdit) {
      const response = await updateMagazine(info);
      const hasErrors = response.length > 0;
      if (data.exportType === "system") {
        toast(hasErrors ? response : "Edición guardada correctamente", {
          type: !hasErrors ? "success" : "error",
        });
        setState({ loading: false });
        return;
      }

      if (data.exportType === "adobe") {
        setCanFetchAdobeStatus(true);
      }
      return;
    }

    setState({ loading: false });
  };

  const handlePickImage = (imageIndex: number) =>
    setState({
      imageIndex,
      uploadType: UploadFileType.ADD_IMAGE,
      showFileManager: true,
    });

  const renderImages = () => {
    return state.images.map((e, i: number) => {
      return (
        <div key={`image-${i}`} className="magazine-item">
          <Image
            src={e.url ? `${process.env.REACT_APP_UPLOADS}/${e.url}` : ""}
          />
          <ButtonGroup className="actions">
            <Button color="gray" onClick={() => handlePickImage(i)}>
              Cambiar imagen
            </Button>
            <Button color="failure" onClick={() => deleteImageRow(i)}>
              Borrar
            </Button>
          </ButtonGroup>
        </div>
      );
    });
  };

  const addImageRow = () =>
    setState({
      images: [
        ...state.images,
        {
          fileId: 0,
          url: "",
        },
      ],
    });

  const deleteImageRow = async (index: number) => {
    if (!id) return;

    const confirm = window.confirm(
      "¿Desea eliminar esta imagen? No se podrá recurar"
    );
    if (!confirm) return;

    const newImages = state.images.filter(
      (image: any, i: number) => i !== index
    );
    setState({ images: newImages });

    const response = await deleteMagazineAttachment(
      parseInt(id),
      state.images[index].fileId
    );
    toast(
      response
        ? "Imagen borrada correctamente"
        : "Ocurrió un error al borrar la imagen",
      {
        type: response ? "success" : "error",
      }
    );
  };

  const handleUploadPdf = () =>
    setState({ uploadType: UploadFileType.ADD_PDF, showFileManager: true });

  const initialize = async (id: number) => {
    const item = await findMagazine(id);
    if (!item) return;

    setValue("name", item.name);
    setValue("category", item.category);
    setValue("published", item.published);
    setValue("image", item.image.id);

    setState({ attachment: item.image });

    if (item.files && item.files.length > 0) {
      setState({
        images: item.files.map((item) => {
          return {
            fileId: item.id,
            url: item.url,
          };
        }),
      });
    }
  };

  const handleAttachmentSelect = () =>
    setState({
      uploadType: UploadFileType.ADD_ATTACHMENT,
      showFileManager: true,
    });

  const removePdf = () =>
    setState({ pdfAttachment: null, showFileManager: false });

  const handleSelectedFile = (file: Attachment) => {
    if (!file) return;

    if (state.uploadType === UploadFileType.ADD_ATTACHMENT) {
      if (file.type !== "image") {
        toast("Por favor selecciona solamente imágenes", {
          type: "warning",
        });
        return;
      }

      setValue("image", file.id);
      setState({ attachment: file, showFileManager: false });
      return;
    }

    if (state.uploadType === UploadFileType.ADD_IMAGE) {
      if (file.type !== "image") {
        toast("Por favor selecciona solamente imágenes", {
          type: "warning",
        });
        return;
      }

      // replace image from index X
      const newImages = [...state.images]; // make a copy

      // replace imge at index state.imageIndex
      newImages[state.imageIndex] = {
        fileId: file.id,
        url: file.url,
      };

      setState({ images: newImages, showFileManager: false });
      return;
    }

    if (state.uploadType === UploadFileType.ADD_PDF) {
      setState({ pdfAttachment: file, showFileManager: false });
      return;
    }
  };

  useEffect(() => {
    (async () => {
      await fetchMagazineCategories();
      if (!id) return;

      const itemId = parseInt(id);
      if (!isNaN(itemId)) {
        await initialize(itemId);
      }
    })();
    // eslint-disable-next-line
  }, []);

  // Adobe service trigger
  /*useEffect(() => {
    if (state.canFetchAdobeStatus) {
      const timer = setInterval(async () => {
        if (state.isAdobeService) {
          clearInterval(timer);
          return;
        }

        const response = await fetch(`${AUTH_URL}/newspaper/downloadAdobeZip`);
        console.log(response, " response adobe");
        if (response) {
          setState({ isAdobeService: true });
        }
      }, 1000);
    }
    // eslint-disable-next-line
  }, [state.canFetchAdobeStatus]);*/
  useEffect(() => {
    console.log(state.isAdobeService, "state adobe");
    console.log(state.canFetchAdobeStatus, "canFetchAdobeStatus");
    if (!state.canFetchAdobeStatus) return;

    const timer = setInterval(async () => {
      try {
        const response = await triggerMagazineDownload();
        console.log(response, "response adobe");

        if (response?.errors && response.errors.length > 0) {
          setState({ isAdobeService: false });
          setCanFetchAdobeStatus(false);
          toast(response.errors.join("\n"), {
            type: "error",
          });
          clearInterval(timer);
        }

        if (response?.status === "done") {
          setState({ isAdobeService: false });
          setCanFetchAdobeStatus(false);
          clearInterval(timer);

          navigate(`${routes.DASHBOARD}/${routes.MAGAZINES}`, {
            replace: true,
          });
        }
      } catch (error) {
        console.error("Error fetching Adobe status:", error);
      }
    }, 30000);

    return () => clearInterval(timer); // Limpiar el intervalo cuando el componente se desmonte o cambie la dependencia

    // eslint-disable-next-line
  }, [state.canFetchAdobeStatus, state.isAdobeService]);

  return (
    <div className="m-4">
      <div className="mb-4">
        <div className="flex flex-row justify-between">
          <h1>Detalles de la edición</h1>
          <Button
            isProcessing={state.loading || loadingMagazines}
            disabled={state.loading || loadingMagazines}
            onClick={handleSubmit(save)}
          >
            Guardar edición
          </Button>
        </div>
      </div>
      <Card>
        <div id="fileUpload" className="max-w-md">
          <div className="mb-2 block">
            <Label htmlFor="file" value="Imagen de la edición" />
          </div>
          {state.attachment && (
            <Card
              className="max-w-sm mb-4"
              imgAlt={state.attachment?.foot}
              imgSrc={`${process.env.REACT_APP_UPLOADS}/${state.attachment?.url}`}
            >
              <p className="font-normal text-gray-700 dark:text-gray-400">
                {`${state.attachment?.source} ${
                  state.attachment?.source !== "" &&
                  state.attachment?.foot !== ""
                    ? "|"
                    : ""
                } ${state.attachment?.foot}`}
              </p>
            </Card>
          )}
          <Button onClick={handleAttachmentSelect}>Elija una imagen</Button>
          {errors && errors.image && (
            <p className="mt-2 text-sm text-red-600 dark:text-red-500">
              {errors.image.message}
            </p>
          )}
        </div>
        <div className="flex flex-col mb-2">
          <Label className="mb-2">Título</Label>
          <TextInput
            placeholder="Escriba aquí"
            {...register("name")}
            helperText={errors && errors.name ? errors.name.message : ""}
            required
            color={errors && errors.name ? "failure" : "gray"}
          />
        </div>
        <div className="flex flex-row mb-0 items-start justify-between gap-4">
          <div className="flex flex-row items-start space-x-4">
            <div className="flex flex-col">
              <div className="mb-2 block">
                <Label htmlFor="category" value="Categoría" />
              </div>
              <Select
                id="category"
                {...register("category")}
                helperText={
                  errors && errors.category ? errors.category.message : ""
                }
                color={errors && errors.category ? "failure" : "gray"}
                required
              >
                <option value="-1">- Elija una opción -</option>
                {magazineCategories &&
                  magazineCategories.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                    </option>
                  ))}
              </Select>
            </div>
            <div className="flex flex-col max-w-md flex-1">
              <div className="mb-2 block">
                <Label htmlFor="published" value="Publicada" />
              </div>
              <Select
                id="published"
                {...register("published")}
                helperText={
                  errors && errors.published ? errors.published.message : ""
                }
                color={errors && errors.published ? "failure" : "gray"}
                required
              >
                <option value="1">Si</option>
                <option value="0">No</option>
              </Select>
            </div>
            <div className="flex flex-col">
              <div className="mb-2 block">
                <Label htmlFor="category" value="Conversión" />
              </div>
              <Select
                id="exportType"
                {...register("exportType")}
                disabled={state.images.length > 0}
                helperText={
                  errors && errors.exportType ? errors.exportType.message : ""
                }
                color={errors && errors.exportType ? "failure" : "gray"}
                required
              >
                <option value="system">Sistema (Recomendado)</option>
                <option value="adobe">Adobe PDF (use con cautela)</option>
              </Select>
            </div>
          </div>
        </div>

        <div className="flex flex-col mb-2">
          <div className="flex flex-row justify-between w-full items-end mb-4">
            <Label className="mb-2">Imágenes</Label>
            <div className="flex flex-row space-x-4">
              <Button
                disabled={state.pdfAttachment !== null}
                onClick={addImageRow}
              >
                <HiPlus className="mr-2 h-5 w-5" />
                Nueva imagen
              </Button>
              {state.pdfAttachment === null && (
                <Popover
                  trigger="hover"
                  placement="left"
                  content={
                    <div className="w-64 text-sm text-gray-500 dark:text-gray-400">
                      <div className="border-b border-gray-200 bg-gray-100 px-3 py-2 dark:border-gray-600 dark:bg-gray-700">
                        <h3
                          id="default-popover"
                          className="font-semibold text-gray-900 dark:text-white"
                        >
                          ¿Cómo funciona?
                        </h3>
                      </div>
                      <div className="px-3 py-2">
                        <p>
                          Seleccione un pdf desde el administrador de archivos,
                          el sistema convertirá dicho archivo en multiples
                          imágenes automáticamente.{" "}
                          <strong>(DESPUES DE GUARDAR)</strong> <br />{" "}
                          <strong>Puede tomar unos minutos en concluir.</strong>
                        </p>
                      </div>
                    </div>
                  }
                >
                  <Button
                    disabled={state.images.length > 0}
                    onClick={handleUploadPdf}
                  >
                    <HiPlus className="mr-2 h-5 w-5" />
                    Seleccionar PDF
                  </Button>
                </Popover>
              )}
              {state.pdfAttachment !== null && (
                <Button color="failure" onClick={removePdf}>
                  <HiTrash className="mr-2 h-5 w-5" />
                  Eliminar PDF
                </Button>
              )}
            </div>
          </div>
          <ImageContainer className="overflow-x-auto mb-4 w-full bg-white border border-gray-200 rounded-lg shadow dark:bg-gray-800 dark:border-gray-700">
            {renderImages()}
          </ImageContainer>
        </div>
      </Card>

      {state.showFileManager && (
        <FileManager
          open={state.showFileManager}
          onClose={handleShowFileManager}
          onSelect={handleSelectedFile}
        />
      )}
      <Overlay style={{ display: state.loading ? "block" : "none" }} />
      <StyledModal style={{ display: state.isAdobeService ? "flex" : "none" }}>
        <h3>
          Por favor espere a que el proceso se complete, esto puede tardar
          varios minutos, dependiendo del tamaño del PDF y su velocidad de
          internet. Por favor, no navegue fuera de la página, esto podría
          interrumpir el proceso y causar errores en el sistema.
        </h3>
        <Spinner size="xl" />
      </StyledModal>
    </div>
  );
};

export default MagazineDetailsPage;
